import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import LoadingOverlay from "../../LoadingOverlay";
import 'moment/locale/fr';
import Header from "../../Header/Header";
import NumsDoss from "../../NumsDoss";
import DocMenu from "../../DocMenu";
import AppointmentForm from './AppointmentForm';
import AppointmentList from './AppointmentList';
import { appointmentConst } from './Const';
import InformationDialog from '../../InformationDialog';
import YesNoDialog from '../../YesNoDialog';
import { getDossierInfos, getAppointments, addAppointment, updateAppointment, deleteAppointment, getVicTrackingFile, getCompanyInfos } from '../Utils';
import { changeTitleName, changePdfFileName, changeIndication } from "../../../redux/actions/emailAction";
import authHeader from '../../Utils';
import history from '../../Routes/history';
import "../../../Styles/Calendar/Appointment.scss";
import EmailEditor from './EmailEditor';
import GenPdf from "../../GenPdf";
import ConfirmationAppointment from '../ConfirmationAppointment';
import VICTrackingPopup from './VICTrackingPopup';

const Appointment = () => {
  const dispatch = useDispatch();
  const dossierValue = useSelector((state) => state.devis.dossierValue);

  const [sendEmail, setSendEmail] = useState(false);
  const [dossierInfos, setDossierInfos] = useState({});
  const [intervenant, setIntervenant] = useState(null);
  const [userId, setUserId] = useState(null);
  const [newAppointment, setNewAppointment] = useState(appointmentConst);
  const [deleteId, setDeleteId] = useState(null);
  const [isAdd, setIsAdd] = useState(true);
  const [isConfirmationAppointment, setIsConfirmationAppointment] = useState(false);
  const [siret, setSiret] = useState('');

  const [lotList, setLotList] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDelDialog, setOpenDelDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');

  const [vicList, setVicList] = useState([]);
  const [vicLoading, setVicLoading] = useState(false);
  const [vicError, setVicError] = useState(null);
  const [savedVicData, setSavedVicData] = useState([]);

  const [openVICPopup, setOpenVICPopup] = useState(false);

  const [file, setFile] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [disableButton, setDisableButton] = useState(false);

  // Récupérer les données VIC sauvegardées
  const fetchSavedVicData = useCallback(async () => {
    if (!dossierValue) return;

    try {
      const response = await fetch(`/api/agenda/getTrackingData/${dossierValue}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des données sauvegardées');
      }

      const json = await response.json();
      if (json.success && json.data) {
        setSavedVicData(json.data);
      } else {
        setSavedVicData([]);
      }
    } catch (error) {
      console.error('Erreur fetchSavedVicData:', error);
      setSavedVicData([]);
    }
  }, [dossierValue]);

  const fetchVICs = useCallback(async () => {
    if (!dossierValue) return;
    setVicLoading(true);
    setVicError(null);
    try {
      const response = await fetch('/api/vic/getVICsNames', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ndossier: dossierValue }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Erreur lors de la récupération des VICs');
      }

      const json = await response.json();
      if (json.success) {
        setVicList(json.files);
      } else {
        setVicList([]);
        setVicError('Aucun fichier VIC trouvé');
      }
    } catch (error) {
      setVicError(error.message);
      setVicList([]);
    } finally {
      setVicLoading(false);
    }
  }, [dossierValue]);

  // Appel automatique lors du changement de dossierValue
  useEffect(() => {
    fetchVICs();
    fetchSavedVicData(); // Récupérer aussi les données sauvegardées
  }, [fetchVICs, fetchSavedVicData]);

  const handleGetAllLots = async () => {
    setIsLoading(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      };

      const res = await fetch("/api/rj/getAllLots", requestOptions);
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      const data = await res.json();
      setLotList(data);
    } catch (error) {
      console.error('Erreur lors de la récupération des lots:', error);
    } finally {
      setIsLoading(false);
    }
  }

  const fetchData = async () => {
    setIsLoading(true);
    const user = authHeader();
    if (user['x-access-token'] === undefined) {
      history.push('/login');
      return;
    }
    setUserId(user.id);

    if (!dossierValue) return;
    if (lotList.length === 0) await handleGetAllLots();

    const result = await getAppointments(dossierValue);
    setAppointments(result);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [dossierValue, lotList.length]);

  useEffect(() => {
    const fetchData = async () => {
      if (!dossierValue) return;
      setIsLoading(true);

      try {
        const result = await getDossierInfos(dossierValue);
        setDossierInfos(result);
      } catch (error) {
        console.error("Erreur dans useEffect:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dossierValue]);

  const handleSortAppointments = (option) => {
    let sortedAppointments = [];

    if (option === 'appointment') {
      sortedAppointments = [...appointments].sort((a, b) => {
        if (!a.appointment_start && !b.appointment_start) {
          return 0;
        }
        if (!a.appointment_start) {
          return sortOrder === 'asc' ? 1 : -1;
        }
        if (!b.appointment_start) {
          return sortOrder === 'asc' ? -1 : 1;
        }
        return sortOrder === 'asc'
          ? new Date(a.appointment_start) - new Date(b.appointment_start)
          : new Date(b.appointment_start) - new Date(a.appointment_start);
      });
    } else {
      sortedAppointments = [...appointments].sort((a, b) => {
        if (!a.intervention_date && !b.intervention_date) {
          return 0;
        }
        if (!a.intervention_date) {
          return sortOrder === 'asc' ? 1 : -1;
        }
        if (!b.intervention_date) {
          return sortOrder === 'asc' ? -1 : 1;
        }
        return sortOrder === 'asc'
          ? new Date(a.intervention_date) - new Date(b.intervention_date)
          : new Date(b.intervention_date) - new Date(a.intervention_date);
      });
    }
    setAppointments(sortedAppointments);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const extractEmails = (inputString) => {
    const emailRegex = /[\w.-]+@[\w-]+(?:\.[\w-]{2,})+/g;
    const emails = inputString.match(emailRegex);

    if (emails) {
      return emails.join(", ");
    } else {
      return "";
    }
  }

  const handleAddAppointment = async () => {
    const { type } = newAppointment;
    if (!type) {
      setOpenDialog(true);
      return
    }
    const newAppointmentFormatedMails = {
      ...newAppointment,
      mail: extractEmails(newAppointment.mail)
    }

    setIsLoading(true);
    await addAppointment(dossierValue, newAppointmentFormatedMails, userId);
    const result = await getAppointments(dossierValue);
    setAppointments(result);
    setNewAppointment(appointmentConst);
    setSiret('');

    await fetchSavedVicData();

    setIsLoading(false);
  };

  const handleUpdateAppointment = async () => {
    const { type } = newAppointment;
    if (!type) {
      setOpenDialog(true);
      return
    }
    const newAppointmentFormatedMails = {
      ...newAppointment,
      mail: extractEmails(newAppointment.mail)
    }
    setIsLoading(true);
    await updateAppointment(newAppointmentFormatedMails);
    const result = await getAppointments(dossierValue, userId);
    setAppointments(result);
    setNewAppointment(appointmentConst);
    setIsAdd(true);
    setSiret('');

    await fetchSavedVicData();

    setIsLoading(false);
  };

  const handleDeleteAppointment = async () => {
    setOpenDelDialog(false);
    setIsLoading(true);
    await deleteAppointment(deleteId);
    const result = await getAppointments(dossierValue, userId);
    setAppointments(result);

    await fetchSavedVicData();

    await setIsLoading(false);
  }

  const handleCloseOpenDialog = () => setOpenDialog(false)
  const handleCloseOpenDelDialog = () => {
    setDeleteId(null);
    setOpenDelDialog(false)
  }

  const handleGetCompanyInfos = useCallback(async () => {
    setIsLoading(true);

    const companySiret = Number(siret.replaceAll(" ", ""));
    const result = await getCompanyInfos(companySiret);

    if (result) {
      const representative = result.dirigeants?.length
        ? result.dirigeants
          .map((d) => [d.prenoms, d.nom].filter(Boolean).join(" "))
          .filter(Boolean)
          .join(", ")
        : "";

      setNewAppointment((prev) => ({
        ...prev,
        company: result.nom_raison_sociale ?? "",
        representative,
      }));
    } else {
      setNewAppointment((prev) => ({
        ...prev,
        company: "",
        representative : ""
      }));
    }

    setIsLoading(false);
  }, [siret]);

  const handleGenClick = useCallback(async () => {
    setOpenVICPopup(true);
  }, []);

  const handleSaveVICTracking = async (vicData) => {
    setDisableButton(true);
    setFile(null);
    setIsLoading(true);

    try {
      const saveResponse = await fetch('/api/agenda/saveTrackingData', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          ndossier: dossierValue,
          vicData: vicData,
        }),
      });

      if (!saveResponse.ok) {
        throw new Error('Erreur lors de la sauvegarde des données');
      }
      setSavedVicData(vicData);

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          ndossier: dossierValue,
          vicData: vicData
        }),
      };

      const response = await fetch("/api/agenda/genVicTracking", requestOptions);

      if (!response.ok) {
        throw new Error(`Erreur réseau : ${response.status} ${response.statusText}`);
      }

      const fileBlob = await response.blob();
      setFile(fileBlob);
      setIsOpen(true);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

    } catch (error) {
      console.error("Erreur lors de la génération du PDF :", error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setDisableButton(false);
      setIsLoading(false);
    }
  };


  const handleShowPdf = useCallback(async () => {
    setDisableButton(true);
    setFile(null);
    let response = await getVicTrackingFile(dossierValue, 'pdf');
    if (response.blob.size > 100) {
      setFile(response.blob);
      setIsOpen(true);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    setDisableButton(false);
  }, [dossierValue]);

  const handleGetWordFile = async () => {
    setDisableButton(true);
    setFile(null);
    let response = await getVicTrackingFile(dossierValue, 'word');
    if (response.blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      setFile(response.blob);
    }
    setDisableButton(false);
  }

  const handleGetPdfFile = async () => {
    setDisableButton(true);
    setFile(null);
    let response = await getVicTrackingFile(dossierValue, 'pdf');
    if (response.blob.size > 100) {
      setFile(response.blob);
      const fileName = response.fileName;
      dispatch(changePdfFileName(fileName))
    }
    setDisableButton(false);
  }

  const handleChangeTitleName = () => {
    dispatch(changeTitleName(`Suivi des VIC`));
  };

  return (
    <div className="appointment">
      {isLoading && <LoadingOverlay />}
      <Header />
      <DocMenu
        file={file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
        updateIndication={() => dispatch(changeIndication('Suivi des VIC'))}
      />
      <h1 className="title">
        {sendEmail ? isConfirmationAppointment ? 'Envoi de la confirmation du rendez-vous' : 'Envoi de mail' : 'Tableau des intervenants'}
      </h1>
      {!sendEmail && <NumsDoss />}
      <div className="container">
        {!sendEmail ?
          <>
            <AppointmentForm
              handleAddAppointment={handleAddAppointment}
              handleUpdateAppointment={handleUpdateAppointment}
              siret={siret}
              setSiret={setSiret}
              handleGetCompanyInfos={handleGetCompanyInfos}
              isAdd={isAdd}
              newAppointment={newAppointment}
              setNewAppointment={setNewAppointment}
              lotList={lotList}
              setIsAdd={setIsAdd}
              handleGenClick={handleGenClick}
              handleShowPdf={handleShowPdf}
              disableButton={disableButton}
            />
            <AppointmentList
              appointments={appointments}
              setOpenDelDialog={setOpenDelDialog}
              setIsAdd={setIsAdd}
              setDeleteId={setDeleteId}
              setNewAppointment={setNewAppointment}
              handleSortAppointments={handleSortAppointments}
              setIntervenant={setIntervenant}
              setIsConfirmationAppointment={setIsConfirmationAppointment}
              setSendEmail={setSendEmail}
            />
          </> : isConfirmationAppointment ?
            <ConfirmationAppointment
              fetchData={fetchData}
              setIsLoading={setIsLoading}
              intervenant={intervenant}
              dossierInfos={dossierInfos}
              setIsConfirmationAppointment={setIsConfirmationAppointment}
              isConfirmationAppointment={isConfirmationAppointment}
              setSendEmail={setSendEmail}
            /> :
            <EmailEditor
              fetchData={fetchData}
              setIsLoading={setIsLoading}
              intervenant={intervenant}
              dossierInfos={dossierInfos}
              setSendEmail={setSendEmail}
            />}

      </div>
      <InformationDialog
        isOpen={openDialog}
        title={'INFORMATION'}
        information={'Veuillez remplir tous les champs'}
        handleClose={handleCloseOpenDialog}
      />
      <YesNoDialog
        isOpen={openDelDialog}
        title={"SUPPRESSION DE L'ITERVENANT"}
        question={`Êtes-vous sûr de vouloir supprimer cet itervenant ?`}
        handleYesClick={handleDeleteAppointment}
        handleClose={handleCloseOpenDelDialog}
      />
      <VICTrackingPopup
        open={openVICPopup}
        onClose={() => setOpenVICPopup(false)}
        appointments={appointments}
        vicList={vicList}
        savedVicData={savedVicData}
        dossierValue={dossierValue}
        lotList={lotList}
        onSave={handleSaveVICTracking}
      />
      <GenPdf
        file={file}
        isOpen={isOpen}
        closeDoc={() => setIsOpen(false)}
        scale={1}
      />
    </div>
  );
};

export default Appointment;